import { Color, DefaultValue, Flex, Icon, Spacing, Text } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import styled from '@emotion/styled'
import React, { cloneElement, isValidElement, ReactElement } from 'react'
import { Control, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Themable } from '../../types/themable'
import { FormLabel, FormLabelProps } from './FormLabel'

const isValidChildElement = (child: ReactElement) => {
  const { formControl } = child.props
  return isValidElement(child) && formControl
}

type FormGroupContainerProps = {
  horizontal?: boolean
}

export const FormGroupContainer = styled.div<FormGroupContainerProps>`
  flex: auto;
  ${({ horizontal }) => {
    if (horizontal) {
      return `
        display: flex;

        label {
          display: none;
        }

        > div {

          input {
            border-radius: 0;
            margin-left: -1px;

            &:focus {
              z-index: 1;
              position: relative;
            }
          }

          &:first-of-type {
            input {
              margin-left: 0px;
              border-top-left-radius: ${DefaultValue.BorderRadius};
              border-bottom-left-radius: ${DefaultValue.BorderRadius};
            }
          }

          &:last-of-type {
            input {
              border-top-right-radius: ${DefaultValue.BorderRadius};
              border-bottom-right-radius: ${DefaultValue.BorderRadius};
            }
          }
        }
      `
    }

    return `
      label {
        display: none;
      }

      div {
        border-radius: 0;
        margin-bottom: 0;

        input {
          margin-top: -1px;

          &:focus {
            z-index: 1;
            position: relative;
          }
        }

        &:first-of-type {
          div {
            border-top-left-radius: ${DefaultValue.BorderRadius};
            border-top-right-radius: ${DefaultValue.BorderRadius};
          }
        }

        &:last-of-type {
          div {
            border-bottom-left-radius: ${DefaultValue.BorderRadius};
            border-bottom-right-radius: ${DefaultValue.BorderRadius};
          }
        }
      }
    `
  }};
`

const TrashIconContainer = styled.div`
  margin-left: 16px;
  padding-top: 5px;
  cursor: pointer;

  &:hover path {
    fill: ${Color.Black};
  }
`

type AddIconContainerProps = FormLabelProps & Themable

const AddIconContainer = styled.div<AddIconContainerProps>`
  color: inherit;
  margin-top: 10px;
  cursor: pointer;

  p {
    color: ${Color.Grey500};
    cursor: pointer;
  }

  &:hover p {
    color: ${Color.Black};
  }

  svg {
    margin-right: ${Spacing.S8};
  }
`

type ControlledFormGroupProps = {
  addButtonLabel?: string
  editable?: boolean
  children: ReactElement[]
  defaultLineValues?: Record<string, any>
  formControl: Control<any>
  horizontal?: boolean
  label?: string
  name: string
  required?: boolean
}

export const ControlledFormGroup = ({
  addButtonLabel,
  editable,
  children,
  defaultLineValues,
  formControl,
  horizontal,
  label,
  name,
  required,
}: ControlledFormGroupProps): ReactElement => {
  const { t } = useTranslation()
  const { fields, append, remove } = useFieldArray({
    control: formControl,
    name: name as string,
  })

  return (
    <div>
      {label && <FormLabel required={required}>{label}</FormLabel>}
      {fields.map((item, index) => (
        <Flex key={item.id}>
          <FormGroupContainer horizontal={horizontal}>
            {children.map((childInput) => {
              if (!isValidChildElement(childInput)) {
                trackError(
                  'Not a valid type of element used in <ControlledFormGroup />. Please check children elements. Valid elements need to support formControl property.',
                )
                return null
              }

              const childName = `${name}[${index}].${childInput.props.name}`

              return cloneElement(childInput, {
                ...childInput.props,
                name: childName,
                key: childName,
                defaultValue: item[childInput.props.name],
                errorPath: childName,
              })
            })}
          </FormGroupContainer>
          {editable && fields.length > 1 && (
            <TrashIconContainer onClick={() => remove(index)}>
              <Icon icon="trash" />
            </TrashIconContainer>
          )}
        </Flex>
      ))}
      {editable && (
        <AddIconContainer onClick={() => append(defaultLineValues || {})}>
          <FormLabel>
            <Icon icon="circlePlus" />
            <Text>{addButtonLabel || t('add')}</Text>
          </FormLabel>
        </AddIconContainer>
      )}
    </div>
  )
}
